import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import Header from "@/components/Header";
import Uploader from "@/components/Uploader";
import TrustStrip from "@/components/TrustStrip";
import ScanningOverlay from "@/components/ScanningOverlay";
import Report from "@/components/Report";
import CopySwitcher from "@/components/CopySwitcher";
import Negotiation from "@/components/Negotiation";
import PhotoStudio from "@/components/PhotoStudio";
import EbaySoldComps from "@/components/EbaySoldComps";
import UpgradeModal from "@/components/UpgradeModal";
import InventoryVault from "@/components/InventoryVault";
import { ShippingCard, SerialCard } from "@/components/ExtraCards";
import SplashScreen from "@/components/SplashScreen";
import PurchaseCelebrationModal from "@/components/PurchaseCelebrationModal";
import Footer from "@/components/Footer";
import HCaptcha, { executeCaptcha } from "@/components/HCaptcha";
import { bumpTotalValue, readTotalValue } from "@/components/ValueFoundTicker";
import { Toaster, toast } from "sonner";
import { apiDemoScan, apiGetCredits, apiScan, apiSyncCredits,
  getSessionId, urlToBase64,
} from "@/lib/api";
import { getDeviceFp, getCachedDeviceFp } from "@/lib/deviceFp";
import { captureReferralOnLoad } from "@/lib/referral";
import { useAuth } from "@/lib/AuthContext";
import { motion } from "framer-motion";

const DEMO_IMAGES = ["/demo_item.jpg", "/demo_item_tag.jpg"];

export default function AppMain() {
  const { user, loading, login } = useAuth();
  const [images, setImages] = useState([null, null, null]);
  const [brandLock, setBrandLock] = useState("");
  // Category is now inferred by the vision model (no user picker).
  const [category] = useState("apparel");
  const [scanning, setScanning] = useState(false);
  const [report, setReport] = useState(null);
  const [scanId, setScanId] = useState(null);
  const [credits, setCredits] = useState(1);
  const [plan, setPlan] = useState("free");
  const [status, setStatus] = useState("inactive");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [vaultKey, setVaultKey] = useState(0);
  const [splashVisible, setSplashVisible] = useState(true);
  const [celebration, setCelebration] = useState(null); // {creditsAdded, tier, balance}
  const [marketAnalytics, setMarketAnalytics] = useState(null);
  // Social-pressure paywall: count total scans this session + fire modal on 3rd.
  const [scanCount, setScanCount] = useState(() => {
    try { return parseInt(localStorage.getItem("rsell_ai_scan_count_v1") || "0", 10) || 0; } catch { return 0; }
  });
  const [socialModal, setSocialModal] = useState(false);

  const sid = useMemo(() => getSessionId(), [user, loading]);

  // Warm the device fingerprint on mount so it's ready before the first scan.
  useEffect(() => { getDeviceFp(); }, []);

  // First-touch affiliate attribution — read ?ref=CODE, drop 90-day cookie.
  useEffect(() => { captureReferralOnLoad(); }, []);

  useEffect(() => {
    if (loading) return;
    apiGetCredits(sid).then((r) => { setCredits(r.credits); setPlan(r.plan); }).catch(() => {});
    setVaultKey((k) => k + 1);
  }, [sid, loading]);

  // Pull-based sync: fires on mount, when returning from Whop (?synced=1), and via the button.
  const runSync = async () => {
    try {
      const r = await apiSyncCredits(sid);
      setCredits(r.balance);
      setPlan(r.plan);
      setStatus(r.status);
      if (r.newCreditsGranted > 0 || (r.newPayments && r.newPayments.length)) {
        const latestTier = r.newPayments?.slice(-1)?.[0]?.tier || r.plan;
        setCelebration({ creditsAdded: r.newCreditsGranted, tier: latestTier, balance: r.balance });
        setVaultKey((k) => k + 1);
      }
      return r;
    } catch {
      return null;
    }
  };

  // Splash + auto-sync on app load
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => setSplashVisible(false), 800);
    runSync();
    // If we just came back from Whop with a query flag, poll a couple more times
    const params = new URLSearchParams(window.location.search);
    if (params.get("synced") === "1") {
      const retry = setTimeout(runSync, 3000);
      return () => { clearTimeout(t); clearTimeout(retry); };
    }
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, sid]);

  const [creditFx, setCreditFx] = useState(0);
  const fireCreditFx = () => setCreditFx((n) => n + 1);

  const canGenerate = useMemo(() => images.filter(Boolean).length >= 2, [images]);

  const [isDemo, setIsDemo] = useState(false);

  const generate = async () => {
    if (!canGenerate) return toast.error("Add at least 2 photos");
    if (credits <= 0 && plan !== "pro") {
      setShowUpgrade(true);
      return;
    }
    setScanning(true);
    setIsDemo(false);
    setReport(null);
    setMarketAnalytics(null);
    try {
      const captchaToken = user ? "" : await executeCaptcha();
      const b64s = images.filter(Boolean).map((x) => x.base64);
      const device_fp = getCachedDeviceFp() || await getDeviceFp();
      const res = await apiScan(sid, brandLock, b64s, category, captchaToken, device_fp);
      setReport(res.report);
      setScanId(res.scan_id);
      if (typeof res.credits_remaining === "number" && res.credits_remaining < credits) fireCreditFx();
      setCredits(res.credits_remaining);
      setPlan(res.plan);
      setVaultKey((k) => k + 1);
      // Bump running value + fire the social-pressure modal on the 3rd scan
      const rec = res?.report?.prices?.recommended;
      if (typeof rec === "number") bumpTotalValue(rec);
      setScanCount((prev) => {
        const next = prev + 1;
        try { localStorage.setItem("rsell_ai_scan_count_v1", String(next)); } catch {}
        if (next === 3 && plan === "free")
        return next;
      });
      toast.success("Report ready");
    } catch (e) {
      const s = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (s === 402 && detail === "device_free_limit_reached") {
        toast.error("You've used your free credits on this device — upgrade to keep going");
        setShowUpgrade(true);
      } else if (s === 402) {
        toast.error("Out of credits");
        setShowUpgrade(true);
      } else if (s === 422 && detail === "low_confidence_retake_photo") {
        toast.error("Couldn't identify with confidence — try a clearer tag photo (your credit is safe)");
      } else {
        toast.error("Scan hiccuped — please try again. No credit was used.");
      }
    } finally {
      setScanning(false);
    }
  };

  const runDemo = async () => {
    setScanning(true);
    setIsDemo(true);
    setReport(null);
    setMarketAnalytics(null);
    try {
      const previews = await Promise.all(
        DEMO_IMAGES.map(async (url) => ({
          preview: url,
          base64: await urlToBase64(url).catch(() => ""),
        }))
      );
      const padded = [...previews];
      while (padded.length < 5) padded.push(null);
      setImages(padded);
      setBrandLock("Ralph Lauren Knit");
      const res = await apiDemoScan();
      await new Promise((r) => setTimeout(r, 1400));
      setReport(res.report);
      setScanId(res.scan_id);
      const rec = res?.report?.prices?.recommended;
      if (typeof rec === "number") bumpTotalValue(rec);
      setScanCount((prev) => {
        const next = prev + 1;
        try { localStorage.setItem("rsell_ai_scan_count_v1", String(next)); } catch {}
        if (next === 3 && plan === "free")
        return next;
      });
      toast.success("Demo scan loaded");
    } catch {
      toast.error("Demo failed to load");
    } finally {
      setScanning(false);
    }
  };

  // One-tap: bump recommended to market avg + refresh dependent numbers/copy.
  const handleAutoAdjust = (avg) => {
    if (!report || typeof avg !== "number" || avg <= 0) return;
    setReport((prev) => {
      if (!prev) return prev;
      const oldRec = prev.prices?.recommended;
      const newRec = Math.round(avg * 100) / 100;
      const fee = Math.round((newRec * 0.1 + 0.4) * 100) / 100;
      const ship = prev.profit?.shipping ?? 0;
      const net = Math.round((newRec - fee - ship) * 100) / 100;
      const patchDesc = (s) => {
        if (typeof s !== "string" || typeof oldRec !== "number") return s;
        const oldStr = `$${Math.round(oldRec)}`;
        const newStr = `$${Math.round(newRec)}`;
        try { return s.split(oldStr).join(newStr); } catch { return s; }
      };
      const desc = prev.descriptions || {};
      return {
        ...prev,
        prices: { ...prev.prices, recommended: newRec },
        profit: { ...prev.profit, list_price: newRec, platform_fee: fee, net },
        descriptions: {
          depop: patchDesc(desc.depop),
          ebay: patchDesc(desc.ebay),
          poshmark: patchDesc(desc.poshmark),
        },
      };
    });
    toast.success(`Adjusted to market avg — $${Math.round(avg)}`);
  };

  return (
    <div className="min-h-screen relative overflow-x-hidden bg-[#F8F9FA]">
      <SplashScreen visible={splashVisible} />
      <div className="animated-grid-bg" aria-hidden="true" />

      <div className="relative z-10">
        <Header
          credits={credits}
          creditFx={creditFx}
          plan={plan}
          onUpgrade={() => setShowUpgrade(true)}
          onSync={runSync}
        />

        <main className="max-w-7xl mx-auto px-4 sm:px-5 md:px-8 pt-6 sm:pt-10 pb-32 sm:pb-40">
          <section className="mb-6 sm:mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 text-[10px] sm:text-[11px] uppercase tracking-[0.16em] font-semibold text-accent-text mb-2 sm:mb-3"
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent-fill"></span>
              rsell ai · Live {user && <span className="text-ink-500 normal-case tracking-normal font-normal">· welcome, {user.name?.split(" ")[0] || "friend"}</span>}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0, transition: { delay: 0.05 } }}
              className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-ink-900 max-w-3xl leading-[1.05]"
            >
              Snap a few photos.<br />
              <span className="hero-line-2">Price like a pro.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0, transition: { delay: 0.1 } }}
              className="text-ink-500 mt-3 sm:mt-4 max-w-xl text-sm sm:text-base"
            >
              rsell ai reads the tag, weighs the comps on eBay, Depop &amp; Mercari, and hands
              you a listing that&apos;s already copy-paste ready.
            </motion.p>
          </section>

          <section className="mb-6 space-y-4">
            <TrustStrip />
            <div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#64748B] font-semibold">
              Upload photos &amp; lock the brand
            </div>
            <Uploader
              plan={plan}
              images={images}
              setImages={setImages}
              brandLock={brandLock}
              setBrandLock={setBrandLock}
              onGenerate={generate}
              onDemo={runDemo}
              scanning={scanning}
              canGenerate={canGenerate}
            />
            <ScanningOverlay active={scanning} />
          </section>

          {report && (
            <section className="tri-tone space-y-8 pt-4" data-testid="results-section">
              {/* Post-scan sign-in CTA — anonymous users see the full report,
                  then get a soft nudge to sign in to save their scan history. */}
              {!user && (
                <div
                  data-testid="post-scan-signin-cta"
                  className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 rounded-2xl bg-[#F1F5F9] border border-[#E9ECEF]"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#0F172A]">Save this scan to your vault</div>
                    <div className="text-xs text-[#64748B] mt-0.5">Sign in with Google — free, instant, keeps your scan history for later.</div>
                  </div>
                  <button
                    data-testid="post-scan-signin-btn"
                    onClick={() => login()}
                    className="w-full sm:w-auto px-5 py-2 rounded-full bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B] transition-colors"
                  >
                    Sign in with Google
                  </button>
                </div>
              )}

              {/* 1. Item + valuation */}
              <div className="space-y-5">
                <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#64748B] flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#10B981]"></span>
                  01 · Valuation
                </div>
                <Report report={report} marketAnalytics={marketAnalytics} onAutoAdjust={handleAutoAdjust} plan={plan} onUpgrade={() => setShowUpgrade(true)} />
              </div>

              {/* 2. Market */}
              <div className="space-y-5">
                <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#64748B] flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#0F172A]"></span>
                  02 · Market
                </div>
                <EbaySoldComps report={report} plan={plan} onUpgrade={() => setShowUpgrade(true)} onAnalytics={setMarketAnalytics} />
              </div>

              {/* 3. List it */}
              <div className="space-y-5">
                <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#64748B] flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#0F172A]"></span>
                  03 · List it
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <ShippingCard shipping={report.shipping} />
                  <SerialCard ocr={report.ocr} />
                </div>
                <CopySwitcher descriptions={report.descriptions} scanId={scanId} />
                <PhotoStudio
              onCharged={() => { setCredits((c) => Math.max(0, c - 1)); fireCreditFx(); }} scanId={scanId} plan={plan} demo={isDemo} category={category} onUpgrade={() => setShowUpgrade(true)} />
                <Negotiation neg={report.negotiation} scanId={scanId} report={report} plan={plan} onUpgrade={() => setShowUpgrade(true)} />
              </div>
            </section>
          )}
        </main>

        <InventoryVault
          sessionId={sid}
          plan={plan}
          refreshKey={vaultKey}
          onUpgrade={() => setShowUpgrade(true)}
        />

        <UpgradeModal
          open={showUpgrade}
          onClose={() => setShowUpgrade(false)}
          currentPlan={plan}
          status={status}
        />

        <Footer />

        <PurchaseCelebrationModal
          open={!!celebration}
          onClose={() => setCelebration(null)}
          creditsAdded={celebration?.creditsAdded ?? 0}
          tier={celebration?.tier}
          balance={celebration?.balance}
        />

        <HCaptcha />
        <Toaster theme="light" position="top-center" richColors />
      </div>
    </div>
  );
}
